import { hideAllPoppers } from "floating-vue";
import type { Driver } from "driver.js";
import { isShown } from "@/composables/guidedTour/anchors";
import { run } from "@/composables/guidedTour/state";

// What's on screen for the step: hideStep and tearDownDriver clear it, so it
// lives with the bubble rather than the run.
interface Display {
  driver: Driver | null;
  stepListeners: (() => void)[];
  onLayoutChange: (() => void) | null;
  onContentChange: (() => void) | null;
  announcer: HTMLElement | null;
}

export const display: Display = {
  driver: null,
  stepListeners: [],
  onLayoutChange: null,
  onContentChange: null,
  announcer: null,
};

let layoutObserver: ResizeObserver | null = null;
let contentObserver: MutationObserver | null = null;

// Lets a drop through the popover to whatever is under it.
function onDragStart() {
  document.body.classList.add("fyr-tour-dragging");
}

function onDragEnd() {
  document.body.classList.remove("fyr-tour-dragging");
}

// Esc closes an open menu or dialog first; only an Esc with nothing open
// ends the tour. Tooltips don't close on Esc, so they don't count.
export const OPEN_MENU = [
  "[data-floating-panel] *",
  "[data-floating-menu] *",
  ".v-popper--theme-dropdown.v-popper__popper--shown",
];
const OPEN_POPUP = [...OPEN_MENU, "#headlessui-portal-root *"].join(",");
let escapeClosedPopup = false;

export function popupOpen(selector = OPEN_POPUP): boolean {
  // Headless UI leaves empty placeholders in its portal root.
  return [...document.querySelectorAll(selector)].some((element) => {
    const { width, height } = element.getBoundingClientRect();
    return width > 0 && height > 0;
  });
}

function onKeydown(event: KeyboardEvent) {
  if (event.key !== "Escape") return;
  // Ended from the keyboard, whatever was clicked before.
  run.pointedInPage = false;
  escapeClosedPopup = popupOpen();
}

function onKeyup(event: KeyboardEvent) {
  if (event.key === "Escape" && escapeClosedPopup) {
    escapeClosedPopup = false;
    event.stopPropagation();
    // floating-vue closes its dropdowns on this keyup, on the popper itself,
    // which no longer gets it (and the bubble has focus anyway).
    hideAllPoppers();
  }
}

function isFocusGuard(
  element: Element | null | undefined,
): element is HTMLElement {
  return (
    element instanceof HTMLElement &&
    element.dataset.headlessuiFocusGuard === "true"
  );
}

export function bubbleButtons(): HTMLButtonElement[] {
  return [
    ...document.querySelectorAll<HTMLButtonElement>(".driver-popover button"),
  ].filter(
    (button) =>
      !button.disabled &&
      button.style.display !== "none" &&
      button.closest("footer")?.style.display !== "none",
  );
}

// Headless UI traps Tab in a dialog and the bubble sits outside it (inside,
// it would be under the overlay), so Tab is routed through the focus guards.
export function bubbleInDialogTabOrder(element: Element): (() => void) | null {
  let trap = element.closest('[role="dialog"]');
  while (
    trap &&
    !(
      isFocusGuard(trap.previousElementSibling) &&
      isFocusGuard(trap.nextElementSibling)
    )
  ) {
    trap = trap.parentElement;
  }
  const guards = [trap?.previousElementSibling, trap?.nextElementSibling];
  if (!guards.every(isFocusGuard)) return null;
  const [before, after] = guards as HTMLElement[];
  let entering = false;
  function onFocus(event: FocusEvent) {
    if (entering || (event.target !== before && event.target !== after)) {
      return;
    }
    const buttons = bubbleButtons();
    if (buttons.length === 0) return;
    event.stopImmediatePropagation();
    (event.target === after ? buttons[0] : buttons[buttons.length - 1]).focus();
  }
  function onKeydown(event: KeyboardEvent) {
    if (event.key !== "Tab") return;
    const buttons = bubbleButtons();
    const at = buttons.indexOf(document.activeElement as HTMLButtonElement);
    if (at === -1 || at !== (event.shiftKey ? 0 : buttons.length - 1)) return;
    event.preventDefault();
    entering = true;
    (event.shiftKey ? after : before).focus();
    entering = false;
  }
  document.addEventListener("focus", onFocus, true);
  document.addEventListener("keydown", onKeydown, true);
  return () => {
    document.removeEventListener("focus", onFocus, true);
    document.removeEventListener("keydown", onKeydown, true);
  };
}

export const BUBBLE = ".driver-popover";

export function pageFocus(): HTMLElement | null {
  const active = document.activeElement;
  return active instanceof HTMLElement &&
    active !== document.body &&
    !active.closest(BUBBLE)
    ? active
    : null;
}

function onPointerDown(event: PointerEvent) {
  run.pointedInPage = !(event.target as Element | null)?.closest?.(BUBBLE);
}

function onFocusIn(event: FocusEvent) {
  if ((event.target as Element | null)?.closest?.(BUBBLE)) {
    run.pointedInPage = false;
  }
}

export function focusBack(element: Element | null): boolean {
  if (
    !(element instanceof HTMLElement) ||
    !element.isConnected ||
    !isShown(element)
  ) {
    return false;
  }
  element.focus();
  if (document.activeElement === element) return true;
  // Container anchors such as KPI cards can't take focus.
  const control = controlsIn(element)[0];
  control?.focus();
  return !!control && document.activeElement === control;
}

// Keeps the cut-out on the target when loading moves it, and re-finds the
// target when the page swaps it for a new element without resizing.
export function followLayout() {
  if (layoutObserver || typeof ResizeObserver === "undefined") return;
  layoutObserver = new ResizeObserver(() => display.onLayoutChange?.());
  layoutObserver.observe(document.body);
  contentObserver = new MutationObserver(() => display.onContentChange?.());
  contentObserver.observe(document.body, { childList: true, subtree: true });
  document.addEventListener("dragstart", onDragStart, true);
  document.addEventListener("dragend", onDragEnd, true);
  document.addEventListener("drop", onDragEnd, true);
  document.addEventListener("keydown", onKeydown, true);
  document.addEventListener("keyup", onKeyup, true);
  document.addEventListener("pointerdown", onPointerDown, true);
  document.addEventListener("focusin", onFocusIn, true);
}

function stopFollowingLayout() {
  layoutObserver?.disconnect();
  layoutObserver = null;
  contentObserver?.disconnect();
  contentObserver = null;
  escapeClosedPopup = false;
  document.removeEventListener("dragstart", onDragStart, true);
  document.removeEventListener("dragend", onDragEnd, true);
  document.removeEventListener("drop", onDragEnd, true);
  document.removeEventListener("keydown", onKeydown, true);
  document.removeEventListener("keyup", onKeyup, true);
  document.removeEventListener("pointerdown", onPointerDown, true);
  document.removeEventListener("focusin", onFocusIn, true);
  onDragEnd();
}

export function hideStep() {
  display.announcer?.remove();
  display.announcer = null;
  display.onLayoutChange = null;
  display.onContentChange = null;
  display.stepListeners.forEach((detach) => detach());
  display.stepListeners = [];
  document.body.classList.remove("fyr-tour-no-overlay", "fyr-tour-popups");
}

export const RAISED_POPUPS =
  "[data-floating-menu], [data-floating-panel], #headlessui-portal-root, .v-popper__popper";

// With focus left in a form field, nothing reads the bubble out. Inside the
// field's dialog, since screen readers ignore what's outside an aria-modal
// one, and filled a moment after it appears so the change is announced.
function announce(bubble: HTMLElement, field: Element) {
  display.announcer?.remove();
  const region = document.createElement("div");
  region.setAttribute("aria-live", "polite");
  region.className = "sr-only";
  (field.closest('[aria-modal="true"]') ?? document.body).append(region);
  display.announcer = region;
  const text = [".driver-popover-title", ".driver-popover-description"]
    .map((selector) => bubble.querySelector(selector)?.textContent?.trim())
    .filter(Boolean)
    .join(". ");
  setTimeout(() => {
    if (display.announcer === region) region.textContent = text;
  }, 100);
}

export function tearDownDriver() {
  const instance = display.driver;
  display.driver = null;
  display.announcer?.remove();
  display.announcer = null;
  stopFollowingLayout();
  instance?.destroy();
}

export function escapeHtml(text: string): string {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

type DriverFactory = typeof import("driver.js").driver;

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function controlsIn(element: Element | undefined): HTMLElement[] {
  if (!element) return [];
  return [
    ...(element.matches(FOCUSABLE) ? [element] : []),
    ...element.querySelectorAll(FOCUSABLE),
  ].filter(
    (control): control is HTMLElement =>
      control instanceof HTMLElement &&
      control.tabIndex >= 0 &&
      getComputedStyle(control).pointerEvents !== "none" &&
      isShown(control),
  );
}

// A control inside another one (a card's ⋮ menu) does something else.
export function inNestedControl(
  target: EventTarget | null,
  within: Element,
): boolean {
  const control = (target as Element | null)?.closest?.(FOCUSABLE);
  const outer = control?.parentElement?.closest(FOCUSABLE);
  return !!outer && within.contains(outer);
}

export function createDriver(driver: DriverFactory, onEsc: () => void): Driver {
  return driver({
    allowClose: true,
    // A stray click on the dimmed page shouldn't end the tour.
    overlayClickBehavior: "none",
    allowKeyboardControl: true,
    animate: !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
    overlayOpacity: 0.45,
    // Lands closer to the click that caused the move.
    duration: 250,
    stagePadding: 6,
    stageRadius: 8,
    smoothScroll: true,
    popoverClass: "fyr-tour",
    closeBtnLabel: "Close tour",
    onDestroyStarted: onEsc,
    // Catches a size change that came while it was moving.
    onHighlighted: () => display.driver?.refresh(),
    onPopoverRender: ({ wrapper, closeButton, nextButton }, { state }) => {
      // Last in Tab order and not the first thing focused: Enter on it
      // would end the tour for someone expecting to move on.
      wrapper.append(closeButton);
      // driver.js makes it a dialog labelled by the title and described by
      // the text; focusable, it is what an action step announces.
      wrapper.tabIndex = -1;
      const step =
        run.activeStep && run.activeStep.tour.steps[run.activeStep.index];
      // driver.js focuses the bubble's first button right after this.
      const before = document.activeElement;
      const target = state.activeElement;
      queueMicrotask(() => {
        const current = run.currentStep;
        // A step's render can land after the next step has begun.
        if (!step || !current) return;
        if (current.tour.steps[current.index] !== step) return;
        if (!wrapper.contains(document.activeElement)) return;
        if (
          nextButton.style.display !== "none" &&
          nextButton.closest("footer")?.style.display !== "none"
        ) {
          nextButton.focus();
          return;
        }
        // Typing in the form the step is about.
        if (before instanceof HTMLElement && target?.contains(before)) {
          before.focus();
          announce(wrapper, before);
          return;
        }
        wrapper.focus();
      });
    },
  });
}
